import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { accessSync, constants } from 'node:fs'
import { delimiter, join } from 'node:path'

const execFileAsync = promisify(execFile)

/**
 * Resolve a command name to its absolute path, searching the system PATH.
 * Works as a pure JS fallback when `which` is not available.
 */
function resolveFromPath(command: string): string | null {
  const envPath = process.env.PATH ?? ''
  const dirs = envPath.split(delimiter)

  for (const dir of dirs) {
    const fullPath = join(dir, command)
    try {
      accessSync(fullPath, constants.X_OK)
      return fullPath
    } catch {
      // not in this dir
    }
  }

  return null
}

/**
 * Resolve a command name to an absolute path.
 *
 * Uses `which` first (POSIX standard, available on macOS and Linux),
 * then falls back to manual PATH search. Returns the absolute path if
 * the command is found, null otherwise.
 *
 * IMPORTANT: pty.spawn() delegates to posix_spawnp which resolves PATH
 * at the OS level. On macOS ARM under npx/npm exec, the OS-level PATH
 * may DIFFER from Node.js's `process.env.PATH`, causing "command not found"
 * failures even when execFile would succeed. Resolving to an absolute
 * path before spawn avoids this entirely.
 */
export async function resolveBinary(command: string): Promise<string | null> {
  // 1. If already an absolute or relative path that exists, use it
  if (command.includes('/')) {
    try {
      accessSync(command, constants.X_OK)
      return command
    } catch {
      return null
    }
  }

  // 2. Use `which` (POSIX standard, most reliable)
  try {
    const { stdout } = await execFileAsync('which', [command], { timeout: 5000 })
    const resolved = stdout.trim()
    if (resolved) {
      return resolved
    }
  } catch {
    // which not available or command not found
  }

  // 3. Manual PATH search as fallback
  return resolveFromPath(command)
}

/**
 * Like resolveBinary, but returns the original command if resolution fails.
 * This allows launchers to still attempt spawning (pty.spawn will fall back
 * to posix_spawnp lookup which may work even when our JS resolution doesn't).
 */
export async function resolveBinaryOrFallback(command: string): Promise<string> {
  const resolved = await resolveBinary(command)
  return resolved ?? command
}
